/* eslint-disable react-refresh/only-export-components */
import { useId, useLayoutEffect, useState, type ReactNode } from "react";

import type { VisualEffectsLevel } from "./visualEffectsPreference";
import { useVisualEffects } from "./useVisualEffects";
import { LiquidGlass } from "./LiquidGlass";
import { LiquidGlassGroup } from "./LiquidGlassGroup";
import { Popover, PopoverContent, PopoverTrigger } from "./ShadcnPopover";
import {
  Label,
  FieldGroup,
  LiveRegion,
  Radio,
  Button,
  visuallyHiddenClassName,
} from "./controls";
import "./VisualEffects.css";

export type { VisualEffectsLevel } from "./visualEffectsPreference";
export { useVisualEffects } from "./useVisualEffects";
export { LiquidGlass } from "./LiquidGlass";
export { LiquidGlassGroup } from "./LiquidGlassGroup";
export type { LiquidGlassProps } from "./LiquidGlass";
export type { LiquidGlassGroupProps } from "./LiquidGlassGroup";

function EffectsSymbol() {
  return (
    <svg
      aria-hidden="true"
      className="ark-effects-symbol"
      fill="none"
      height="1em"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1.7"
      viewBox="0 0 24 24"
      width="1em"
    >
      <path d="m13 2-9 12h7l-1 8 10-12h-7l1-8Z" />
    </svg>
  );
}

const levels: {
  value: VisualEffectsLevel;
  label: string;
  description: string;
}[] = [
  {
    value: "rich",
    label: "液态玻璃",
    description: "内容明暗适配、曲面折射与玻璃融合",
  },
  {
    value: "frosted",
    label: "毛玻璃",
    description: "背景扩散模糊、柔和染色与细腻纹理",
  },
  { value: "standard", label: "自发光", description: "柔光边缘与发光反馈" },
  { value: "lite", label: "轻量", description: "轻微高光，无背景模糊" },
  { value: "off", label: "纯色", description: "不透明界面，无装饰特效" },
];

export function VisualEffectsProvider({ children }: { children: ReactNode }) {
  const { level, reducedMotion, reducedTransparency } = useVisualEffects();
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.effects = level;
    root.dataset.effectsMotion = reducedMotion ? "reduced" : "full";
    root.dataset.effectsTransparency = reducedTransparency ? "reduced" : "full";
  }, [level, reducedMotion, reducedTransparency]);
  return children;
}

export function VisualEffectsSettings() {
  const [expanded, setExpanded] = useState(false);
  const { level, setLevel, reducedMotion, reducedTransparency } =
    useVisualEffects();
  const id = useId();
  return (
    <div className="ark-effects-settings">
      <div className="ark-effects-heading">
        <strong>视觉特效</strong>
        <span>立即生效 · 保存在当前设备</span>
      </div>
      <FieldGroup
        className="ark-effects-options"
        legend="特效等级"
        legendProps={{ className: visuallyHiddenClassName }}
      >
        {levels.map((option) => (
          <Label className="ark-effects-option" key={option.value}>
            <Radio
              checked={level === option.value}
              name={`visual-effects-${id}`}
              onChange={() => setLevel(option.value)}
              size="small"
              value={option.value}
            />
            <span className="ark-effects-option-copy">
              <strong>{option.label}</strong>
              <small>{option.description}</small>
            </span>
            <span aria-hidden="true" className="ark-effects-check" />
          </Label>
        ))}
      </FieldGroup>
      <div aria-label="玻璃材质预览" className="ark-effects-preview">
        <span aria-hidden="true" className="ark-effects-preview-orbit" />
        <LiquidGlassGroup className="ark-effects-preview-group" clear
          data-expanded={expanded ? "true" : "false"}>
          <LiquidGlass className="ark-effects-preview-glass" clear>
            <EffectsSymbol />
            <span>灵感，流动起来。</span>
          </LiquidGlass>
          <LiquidGlass className="ark-effects-preview-control" clear>
            <Button aria-expanded={expanded} aria-label={expanded ? "收起玻璃预览" : "展开玻璃预览"}
              onClick={() => setExpanded((value) => !value)} htmlType="button" type="unstyled">
              <span aria-hidden="true">{expanded ? "−" : "+"}</span>
              {expanded ? <span>收起</span> : null}
            </Button>
          </LiquidGlass>
        </LiquidGlassGroup>
      </div>
      <LiveRegion as="p" className="ark-effects-note">
        {reducedTransparency
          ? "已按系统偏好使用不透明材质。"
          : reducedMotion
            ? "已按系统偏好减少动态效果。"
            : level === "rich"
              ? "液态玻璃适合性能充足的设备；折射随浏览器支持降级。"
              : "内容区域保持清晰，特效用于导航和控制浮层。"}
      </LiveRegion>
    </div>
  );
}

export function VisualEffectsButton() {
  const [open, setOpen] = useState(false);
  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        aria-label="设置视觉特效"
        className="ark-effects-trigger"
        title="视觉特效"
        type="button"
      >
        <EffectsSymbol />
      </PopoverTrigger>
      <PopoverContent aria-label="视觉特效" side="bottom">
        <VisualEffectsSettings />
      </PopoverContent>
    </Popover>
  );
}
